import { Link } from "react-router-dom";
import { useState } from "react";
import { ChevronRight, Wrench, Building2, Mountain, Truck, Package, Hammer, HardHat, Globe, Check, X, MessageCircle, ArrowRight } from "lucide-react";
import { IMAGES, HERO_VIDEO, HERO_VIDEO_FALLBACK, whatsappLink } from "@/data/site";
import LogoMarquee from "@/components/LogoMarquee";
import TrustBadgesStrip from "@/components/TrustBadgesStrip";
import { SectionHeading, Reveal } from "@/components/Section";
import { useCountUp } from "@/hooks/useReveal";

const HeroHeadline = () => {
  const words = ["YOUR", "BUILD", "NEVER", "STOPS."];
  return (
    <h1 className="font-display text-[58px] sm:text-[84px] md:text-[104px] leading-[0.92] text-white">
      {words.map((w, i) => (
        <span
          key={w}
          className="inline-block mr-[0.18em]"
          style={{
            animation: `cg-word-up .8s cubic-bezier(.2,.7,.2,1) both`,
            animationDelay: `${i * 0.15}s`,
          }}
        >{w}</span>
      ))}
      <style>{`@keyframes cg-word-up { from { opacity:0; transform: translateY(40px); } to { opacity:1; transform: translateY(0); } }`}</style>
    </h1>
  );
};

const Stat = ({ value, suffix = "", label }) => {
  const [ref, n] = useCountUp(value, 1600);
  return (
    <div ref={ref} className="text-center md:text-left" data-testid={`stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="stat-number">{n}{suffix}</div>
      <div className="mt-2 text-[12px] font-bold tracking-[.18em] text-[#0A1628]">{label}</div>
    </div>
  );
};

const Faq = ({ q, a, id }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`faq-${id}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left text-white"
        data-testid={`faq-toggle-${id}`}
      >
        <span className="font-display text-[20px] md:text-[22px] tracking-wide pr-6">{q}</span>
        <span className={`text-[#E8721C] text-3xl leading-none transition-transform ${open ? "rotate-45" : ""}`}>+</span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96 pb-6" : "max-h-0"}`}>
        <p className="text-[#8A96A3] text-[16px] leading-7 pr-10">{a}</p>
      </div>
    </div>
  );
};

const NumberCard = ({ s, i }) => {
  const [r, v] = useCountUp(s.n, 1500);
  return (
    <div ref={r} className="text-center md:text-left" data-testid={`btn-stat-${i}`}>
      <div className="font-display text-[#E8721C] text-[64px] md:text-[80px] leading-none">{v}{s.suf}</div>
      <div className="mt-3 text-white font-bold tracking-[.14em] text-[12px]">{s.l}</div>
      <div className="mt-2 text-[#8A96A3] text-[13px] leading-5">{s.s}</div>
    </div>
  );
};

const BY_NUMBERS = [
  { n: 23, suf: "+", l: "YEARS IN BUSINESS", s: "Operating since 2003 — 23 years of trusted supply." },
  { n: 500, suf: "+", l: "PRODUCT CATEGORIES", s: "Hardware, building, mining, heavy, bulk." },
  { n: 4, suf: "", l: "MAJOR CLIENT BRANDS", s: "Build It, Mica, SI Hardware, Dada's World." },
  { n: 1, suf: "", l: "ACTIVE ACROSS AFRICA", s: "Established trading reach across the continent." },
  { n: 100, suf: "%", l: "B2B FOCUS", s: "Contractors, retailers and importers only." },
];

const categories = [
  { Icon: Wrench, name: "HARDWARE & TOOLS", desc: "Hand tools, power tools, fasteners, safety gear and all hardware essentials.", img: IMAGES.hardware },
  { Icon: Building2, name: "BUILDING MATERIALS", desc: "Cement, steel, bricks, roofing, tiles, doors, windows — the full build cycle.", img: IMAGES.cement },
  { Icon: Mountain, name: "MINING EQUIPMENT", desc: "Industrial mining tools, safety gear, ground support and specialist supplies.", img: IMAGES.mining },
  { Icon: Truck, name: "HEAVY EQUIPMENT", desc: "Earthmoving, material handling, generators, compressors and industrial machinery.", img: IMAGES.heavy },
  { Icon: Package, name: "COMMODITIES & BULK", desc: "Steel products, copper, construction chemicals and bulk commodity supply.", img: IMAGES.steel },
  { Icon: Hammer, name: "RENOVATION & BUILD", desc: "We don't just supply — we also build, renovate and complete turn-key projects.", img: IMAGES.renoBath },
];

const PAIN_POINTS = [
  "Dealing with 6 different suppliers for one project",
  "Materials arrive late and your crew sits idle",
  "Can't find one partner for hardware, steel AND mining equipment",
  "Looking for a trusted export and trading partner with proper documentation",
  "Paying retail prices when you should be buying wholesale",
];
const SOLUTIONS = [
  "One supplier. Every category. Foundation to roof.",
  "Reliable delivery — your project never waits on us",
  "500+ product categories from hardware to heavy machinery",
  "Established export and trading partner — clients in Malawi, Tanzania and across Africa",
  "Wholesale pricing for B2B clients",
];
const HOW_IT_WORKS = [
  { n: "01", t: "TELL US WHAT YOU NEED", d: "Call, WhatsApp or complete our quote form. Tell us your requirements — product type, quantities, location and timeline." },
  { n: "02", t: "WE PREPARE YOUR QUOTE", d: "Within 24 hours we send you a detailed quote. Competitive pricing, clear product specs, and delivery timeline included." },
  { n: "03", t: "WE DELIVER. YOU BUILD.", d: "Products delivered to your site, warehouse or freight agent. For exports we handle documentation and freight." },
];
const WHY_23_YEARS = [
  "Deep supplier relationships = better pricing",
  "Industry knowledge you can't fake",
  "A reputation we protect with every order",
  "We'll still be here next year, and the year after",
];
const PHOTO_STORY = [
  { img: IMAGES.steelStructure, t: "STRUCTURAL STEEL" },
  { img: IMAGES.cement, t: "BUILDING MATERIALS" },
  { img: IMAGES.mining, t: "MINING EQUIPMENT" },
  { img: IMAGES.heavy, t: "HEAVY EQUIPMENT" },
  { img: IMAGES.steel, t: "COMMODITIES & BULK" },
  { img: IMAGES.exportContainers, t: "EXPORT READY" },
];
const RENO_POINTS = ["Full house renovations","New construction","Commercial projects","We supply our own materials = lower costs"];

const HeroSection = () => (
  <section className="relative bg-[#0A1628] blueprint-grid corner-accent overflow-hidden" data-testid="home-hero">
    <video
      className="hero-video"
      autoPlay
      loop
      muted
      playsInline
      poster={IMAGES.heroPoster}
      aria-hidden="true"
      data-testid="hero-video"
    >
      <source src={HERO_VIDEO} type="video/mp4" />
      <source src={HERO_VIDEO_FALLBACK} type="video/mp4" />
    </video>
    <div className="hero-video-overlay"/>
    <div className="relative z-[2] max-w-[1400px] mx-auto px-5 lg:px-10 pt-16 lg:pt-24 pb-32 grid lg:grid-cols-12 gap-10 items-center">
      <div className="lg:col-span-7">
        <div className="text-[#E8721C] text-[12px] font-bold tracking-[.22em] mb-5" data-testid="hero-eyebrow">
          ESTABLISHED TRADING & SUPPLY COMPANY · HEADQUARTERED IN PRETORIA · EST. 2003
        </div>
        <HeroHeadline />
        <p className="mt-7 text-white/85 text-[18px] md:text-[20px] max-w-2xl leading-7">
          Hardware. Building Materials. Mining Equipment. Heavy Machinery. Commodities. Renovation &amp; Construction.
          <span className="text-white"> 23 years of trusted supply</span> — to contractors, retailers and importers across Africa and beyond.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/contact" className="btn-orange btn-pulse" data-testid="hero-quote-btn">Request a Quote <ArrowRight size={16}/></Link>
          <Link to="/what-we-supply" className="btn-outline-white" data-testid="hero-supply-btn">View What We Supply</Link>
        </div>
        <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-[12px] text-white/80 font-semibold">
          <span className="flex items-center gap-2"><Check size={14} className="text-[#E8721C]"/> 23 Years in Business</span>
          <span className="flex items-center gap-2"><Check size={14} className="text-[#E8721C]"/> Full Range — Foundation to Roof</span>
          <span className="flex items-center gap-2"><Check size={14} className="text-[#E8721C]"/> Active Export Clients Across Africa</span>
          <span className="flex items-center gap-2"><Check size={14} className="text-[#E8721C]"/> B2B Specialists</span>
        </div>
      </div>
      <div className="lg:col-span-5 hidden lg:block">
        <div className="relative">
          <div className="absolute -top-3 -left-3 right-3 bottom-3 border-2 border-[#E8721C]"/>
          <img src={IMAGES.warehouse} alt="Warehouse and construction supply" className="relative w-full h-[520px] object-cover" loading="eager"/>
        </div>
      </div>
    </div>
  </section>
);

const ProblemSolutionSection = () => (
  <section className="bg-[#F8F6F2] py-24" data-testid="problem-solution">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid md:grid-cols-2 gap-8">
      <Reveal>
        <div className="cg-card-dark">
          <h3 className="font-display text-[28px] md:text-[34px] heading-bar text-white">SOUND FAMILIAR?</h3>
          <ul className="mt-6 space-y-3 text-[#cbd3df]">
            {PAIN_POINTS.map((t) => (
              <li key={t} className="flex gap-3"><X className="text-[#E8721C] mt-1 shrink-0" size={18}/><span>{t}</span></li>
            ))}
          </ul>
        </div>
      </Reveal>
      <Reveal delay={120}>
        <div className="cg-card relative" style={{background:"#fff"}}>
          <h3 className="font-display text-[28px] md:text-[34px] heading-bar text-[#0A1628]">THAT'S EXACTLY WHY WE EXIST.</h3>
          <ul className="mt-6 space-y-3 text-[#1E2D40]">
            {SOLUTIONS.map((t) => (
              <li key={t} className="flex gap-3"><Check className="text-[#E8721C] mt-1 shrink-0" size={18}/><span>{t}</span></li>
            ))}
          </ul>
        </div>
      </Reveal>
    </div>
  </section>
);

const WhoWeServeSection = () => (
  <section className="bg-[#0A1628] py-24 blueprint-grid" data-testid="who-we-serve">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="WHO WE WORK WITH" title="TWO AUDIENCES. ONE SUPPLIER." light />
      <div className="grid md:grid-cols-2 gap-8">
        <Reveal>
          <div className="cg-card-dark h-full" style={{background:"#1E2D40"}}>
            <HardHat className="text-[#E8721C]" size={42}/>
            <h3 className="font-display text-[26px] mt-4 text-white">CONTRACTORS & RETAILERS</h3>
            <p className="mt-3 text-[#cbd3df] leading-7">You need one reliable supply partner for everything. Hardware, building materials, mining equipment and heavy machinery — all on one account. We have been doing this for 23 years. We know what contractors and retailers need and we deliver it.</p>
            <div className="mt-5 text-[12px] tracking-[.16em] text-[#8A96A3] font-bold">BUILD IT · MICA · SI HARDWARE · DADA'S WORLD OF HARDWARE</div>
            <Link to="/for-contractors" className="btn-orange mt-7" data-testid="serve-contractors-btn">See What We Supply <ArrowRight size={14}/></Link>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="cg-card-dark cg-card-gold h-full" style={{background:"#1E2D40"}}>
            <Globe className="text-[#C8973C]" size={42}/>
            <h3 className="font-display text-[26px] mt-4 text-white">IMPORTERS & INTERNATIONAL BUYERS</h3>
            <p className="mt-3 text-[#cbd3df] leading-7">You need a trusted trading and supply partner who can source quality products and get them to you wherever you are. We have the range, the supplier relationships and the export experience to make it happen. We have established export clients in Malawi, Tanzania and across Africa.</p>
            <div className="mt-5 text-[12px] tracking-[.16em] text-[#C8973C] font-bold">ACTIVE ACROSS AFRICA · OPEN TO GLOBAL ENQUIRIES</div>
            <Link to="/export-import" className="btn-gold mt-7" data-testid="serve-exporters-btn">Explore Export Services <ArrowRight size={14}/></Link>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const SupplyOverviewSection = () => (
  <section className="bg-[#F8F6F2] py-24" data-testid="what-we-supply-overview">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading
        label="WHAT WE SUPPLY"
        title="EVERYTHING FROM THE GROUND UP"
        subtitle="500+ product categories. One supplier. Zero compromises. From the first foundation block to the final roof tile."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map((c, i) => (
          <Reveal key={c.name} delay={i*80}>
            <div className="cg-card-dark group overflow-hidden p-0" data-testid={`cat-${c.name.toLowerCase().replace(/\s|&/g,'-')}`}>
              <div className="relative h-44 overflow-hidden">
                <img src={c.img} alt={c.name} loading="lazy" className="w-full h-full object-cover opacity-50 group-hover:opacity-70 group-hover:scale-105 transition-all duration-500"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[#1E2D40] via-[#1E2D40]/40 to-transparent"/>
                <c.Icon className="absolute top-4 right-4 text-[#E8721C]" size={28}/>
              </div>
              <div className="p-6">
                <h4 className="font-display text-[22px] tracking-wide">{c.name}</h4>
                <p className="mt-2 text-[#cbd3df] text-[14px] leading-6">{c.desc}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10">
        <Link to="/what-we-supply" className="inline-flex items-center gap-2 text-[#E8721C] font-bold tracking-[.14em] text-sm hover:gap-3 transition-all" data-testid="full-product-link">
          GET THE FULL PRODUCT LIST <ChevronRight size={16}/>
        </Link>
      </div>
    </div>
  </section>
);

const HowItWorksSection = () => (
  <section className="bg-[#0A1628] py-24 blueprint-grid" data-testid="how-it-works">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading label="STARTING IS SIMPLE" title="HOW IT WORKS" light />
      <div className="grid md:grid-cols-3 gap-8 relative">
        {HOW_IT_WORKS.map((s, i) => (
          <Reveal key={s.n} delay={i*120}>
            <div className="relative">
              <div className="font-display text-[88px] text-[#E8721C]/30 leading-none">{s.n}</div>
              <h4 className="font-display text-[22px] text-white mt-1 heading-bar">{s.t}</h4>
              <p className="mt-3 text-[#8A96A3] leading-7 text-[15px]">{s.d}</p>
            </div>
          </Reveal>
        ))}
      </div>
      <div className="mt-10"><Link to="/contact" className="btn-orange" data-testid="how-quote-btn">Start Your Quote Now</Link></div>
    </div>
  </section>
);

const StatsStripSection = () => (
  <section className="bg-[#F8F6F2] py-20" data-testid="stats-strip">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
      <Stat value={23} suffix="+" label="YEARS IN BUSINESS"/>
      <Stat value={500} suffix="+" label="PRODUCT CATEGORIES"/>
      <Stat value={1} suffix="" label="ACTIVE ACROSS AFRICA"/>
      <Stat value={100} suffix="%" label="B2B FOCUSED"/>
    </div>
  </section>
);

const RenovationSnapshotSection = () => (
  <section className="bg-[#0A1628] text-white blueprint-grid" data-testid="renovation-snapshot">
    <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 items-stretch">
      <div className="px-5 lg:px-10 py-20">
        <div className="text-[#E8721C] text-[12px] font-bold tracking-[.22em] mb-4">WE ALSO BUILD</div>
        <h3 className="font-display text-[40px] md:text-[52px] heading-bar leading-[1]">FROM SUPPLY TO STRUCTURE — WE DO IT ALL.</h3>
        <p className="mt-6 text-[#cbd3df] leading-7 max-w-xl">Our Renovation &amp; Construction division takes projects from the ground up. House renovations, new builds, commercial fit-outs and full construction projects. We supply our own materials — which means your build costs less.</p>
        <ul className="mt-7 space-y-2 text-[15px]">
          {RENO_POINTS.map((t)=> (
            <li key={t} className="flex gap-3"><Check className="text-[#E8721C]" size={18}/><span>{t}</span></li>
          ))}
        </ul>
        <Link to="/renovation-construction" className="btn-orange mt-8" data-testid="reno-cta">View Renovation Services <ArrowRight size={14}/></Link>
      </div>
      <div className="relative min-h-[360px] lg:min-h-full">
        <img src={IMAGES.contractor} alt="Construction team reviewing plans on an active site" loading="lazy" className="absolute inset-0 w-full h-full object-cover"/>
        <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#0A1628]/40"/>
      </div>
    </div>
  </section>
);

const Why23YearsSection = () => (
  <section className="bg-[#F8F6F2] py-24" data-testid="why-23-years">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading title="WHEN YOU CHOOSE A 23-YEAR PARTNER…" subtitle="Here's what you actually get when you work with a company that's been doing this since 2003." />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {WHY_23_YEARS.map((t,i) => (
          <Reveal key={t} delay={i*80}><div className="cg-card h-full"><div className="font-display text-[44px] text-[#E8721C] leading-none">0{i+1}</div><p className="mt-3 text-[#1E2D40] leading-7">{t}</p></div></Reveal>
        ))}
      </div>
    </div>
  </section>
);

const PhotoStorySection = () => (
  <section className="bg-[#0A1628] py-0" data-testid="photo-story">
    <div className="grid grid-cols-2 md:grid-cols-3">
      {PHOTO_STORY.map((p) => (
        <div key={p.t} className="relative h-[220px] md:h-[280px] overflow-hidden group">
          <img src={p.img} alt={p.t} loading="lazy" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"/>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/30 to-transparent"/>
          <div className="absolute bottom-5 left-5 right-5 font-display text-white text-[18px] md:text-[22px] tracking-wide border-l-[3px] border-[#E8721C] pl-3">{p.t}</div>
        </div>
      ))}
    </div>
  </section>
);

const ByNumbersSection = () => (
  <section className="bg-[#0A1628] py-24 blueprint-grid text-white" data-testid="by-numbers">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10">
      <SectionHeading light label="BY THE NUMBERS" title="REAL FIGURES. NO MARKETING FLUFF." subtitle="We don't do testimonials we can't verify. Here is what 23 years of focused B2B supply actually looks like." />
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-y-10 gap-x-8 mt-10">
        {BY_NUMBERS.map((s, i) => <NumberCard key={s.l} s={s} i={i} />)}
      </div>
    </div>
  </section>
);

const FaqSection = () => (
  <section className="bg-[#0A1628] py-24 blueprint-grid" data-testid="faq-section">
    <div className="max-w-[1100px] mx-auto px-5 lg:px-10">
      <SectionHeading title="QUESTIONS WE GET ASKED" light />
      <div>
        <Faq id="b2b" q="Do you supply individuals or only businesses?" a="We are primarily a B2B supplier. We work with contractors, hardware retailers, building companies and exporters. If you're a serious buyer — individual or business — get in touch and we'll assess your needs."/>
        <Faq id="min" q="What is your minimum order?" a="We're flexible. From single-line orders to full container loads. Tell us what you need and we'll make it work."/>
        <Faq id="export" q="Can you export to my country?" a="Yes. We are active across Africa and open to enquiries from anywhere in the world. We handle export documentation and freight coordination."/>
        <Faq id="delivery" q="How quickly can you deliver?" a="Most domestic orders are processed within 24–72 hours. Export orders vary by destination — we'll confirm timelines in your quote."/>
        <Faq id="reno" q="Do you handle the full renovation or just supply materials?" a="Both. Our Renovation & Construction division handles full projects — from the first brick to the final tile. Or we can supply only the materials if you have your own contractor."/>
        <Faq id="quote" q="How do I get a quote?" a="Complete the quote form on our Contact page, call us directly or WhatsApp us. We respond within 24 business hours."/>
        <Faq id="pricing" q="Are your prices competitive?" a="Yes. We work directly with manufacturers and major distributors. Our B2B pricing reflects volume and long-term relationships — not retail markups."/>
      </div>
    </div>
  </section>
);

const FinalCtaSection = () => (
  <section className="bg-[#E8721C]" data-testid="final-cta">
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-20 grid md:grid-cols-2 gap-8 items-center">
      <h3 className="font-display text-[40px] md:text-[58px] text-[#0A1628] leading-[1]">READY TO STOP SEARCHING AND START BUILDING?</h3>
      <div>
        <p className="text-[#0A1628]/85 text-[17px] leading-7">Join one of Africa's most trusted B2B supply networks. 23 years of quality, delivery and competitive pricing.</p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/contact" className="btn-navy" data-testid="final-quote-btn">Request a Quote</Link>
          <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white text-[#E8721C] font-bold tracking-[.06em] uppercase text-[14px] px-7 py-[14px] border-2 border-white hover:bg-[#0A1628] hover:text-white transition-colors">
            <MessageCircle size={16}/> WhatsApp Us Now
          </a>
        </div>
      </div>
    </div>
  </section>
);

const Home = () => (
  <>
    <HeroSection />
    <TrustBadgesStrip />
    <LogoMarquee />
    <ProblemSolutionSection />
    <WhoWeServeSection />
    <SupplyOverviewSection />
    <HowItWorksSection />
    <StatsStripSection />
    <RenovationSnapshotSection />
    <Why23YearsSection />
    <PhotoStorySection />
    <ByNumbersSection />
    <FaqSection />
    <FinalCtaSection />
  </>
);

export default Home;
